/* eslint-disable sort-keys */
import { useMutation, useQuery } from '@apollo/client'
import {
  DefaultButton,
  FormErrorLabel,
  FormGrid,
  FormGridContainer,
  FormGroup,
  FormInput,
  FormLabel,
  FormSelect,
  FormInputWithSymbol,
  FormBreak,
  FormToggle,
  CloseButton,
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalTitle,
} from '@patchworkhealth/web-components'
import { useFormik } from 'formik'
import React, { useState } from 'react'
import Modal from 'react-bootstrap/Modal'
import { toast } from 'react-toastify'
import styled from 'styled-components'
import * as Yup from 'yup'
import { parseTimeInTZ } from '../../../Helpers/momentHelpers'
import {
  errorToast,
  loadingToast,
  successToast,
  GRADES,
  securityClearance,
  SHIFT_BLOCK_CREATE,
} from '../helpers/BlockBookingsHelpers'
import { ShiftModalTitle } from '../helpers/BlockComponents'
import { DateInput } from '../helpers/dateInput'

export const IntercomHelpers = [
  'Please input your name in this field. Once the Demand Order is raised the BMS will assign the Demand Order (DO) Unique Ref Number',
  'Select the required staff group. Please note Dentists and Doctors are included in the Medical Group',
  'Select required Job Description',
  'Please Complete if required',
  'Please input the financial approval code in this field. E.G. KA0 B00 234123',
  'Please input JPA/MyHR position number in this field',
  'Please select BPSS unless your location dictates otherwise',
  "When creating a new Demand Order, please include reporting details (E.G.’s first day contact name and telephone number). Please also confirm if the mess is potentially available and if so, please include the Mess Manager's name and telephone number.",
  'Do not use',
  'Sites means Regions',
  'Units means Locations',
  'Please input the correct break time EG 30,45,60 Minutes as required',
  'Insert the UIN for the location that is picking up the costs for the Demand Order',
  'Please include information to support the approval, e.g. financial approval code',
]

export const modHelperTextFunction = (index, left) => {
  return (
    <ModWrapper>
      <HelpIcon />
      <ToolTip left={left}>{IntercomHelpers[index]}</ToolTip>
    </ModWrapper>
  )
}

function CreateBlockModal({ handleClose, refetch, inputs, details }) {
  // STATE & GQL ********************************

  const { modOrganisation, showCreateModal, organisation } = inputs

  const [validate, setValidate] = useState(false)
  const [skipGradesQuery, setSkipGrGradesQuery] = useState(true)
  const [grades, setGrades] = useState([])

  const sortedGrades = [...grades]?.sort((a, b) => a.label.localeCompare(b.label))

  const [createShift] = useMutation(SHIFT_BLOCK_CREATE)
  // Formik ***********************************

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      adminNotes: '',
      bookingPreference: false,
      endDate: '',
      grade: [],
      // Mod
      modAccommodationExpensesBudget: '',
      modApprovalBudgetValue: '',
      modFinancialApprovalCode: '',
      modOutOfHoursBudget: '',
      modOutOfHoursEstimatedWeeklyHours: '',
      modPositionNumber: '',
      modSecurityClearance: [],
      modSubsistenceExpensesBudget: '',
      modTravelExpensesBudget: '',
      //
      name: '',
      reason: [],
      skills: null,
      staffGroup: [],
      startDate: '',
      workerNotes: '',
    },
    validateOnChange: validate,
    validationSchema: Yup.object({
      endDate: Yup.string().required('Required'),
      grade: Yup.array().min(1, 'Required'),
      name: Yup.string().min(2, 'Too Short!').max(50, 'Too Long!').required('Required'),
      reason: Yup.array().min(1, 'Required'),
      staffGroup: Yup.array().min(1, 'Required'),
      startDate: Yup.string().min(1, 'Please enter').required('Required'),
      ...(modOrganisation && {
        modApprovalBudgetValue: Yup.number().required('Required'),
        modFinancialApprovalCode: Yup.string().required('Required'),
        modOutOfHoursBudget: Yup.number(),
        modOutOfHoursEstimatedWeeklyHours: Yup.number(),
        modPositionNumber: Yup.string().required('Required'),
        modSecurityClearance: Yup.array().min(1, 'Required'),
      }),
    }),
    onSubmit: (values, { resetForm }) => {
      toast.loading('Loading', loadingToast)

      const baseVariables = {
        name: values.name,
        gradeId: Number(values.grade[0].value),
        staffGroupId: Number(values.staffGroup[0].value),
        organisationId: organisation.id,
        reasonForBookingId: values.reason[0].value,
        adminNotes: values.adminNotes,
        workerNotes: values.workerNotes,
        bookingPreference: values.bookingPreference,
        startDate: parseTimeInTZ(values.startDate).startOf('day').format('YYYY-MM-DDTHH:mm:00'),
        endDate: parseTimeInTZ(values.endDate).endOf('day').format('YYYY-MM-DDTHH:mm:00'),
      }

      let modVariables = {}
      if (modOrganisation) {
        modVariables = {
          modApprovalBudgetValue: values.modApprovalBudgetValue,
          modFinancialApprovalCode: values.modFinancialApprovalCode,
          modPositionNumber: values.modPositionNumber,
          modSecurityClearance: values.modSecurityClearance[0].value,

          ...(values.modOutOfHoursEstimatedWeeklyHours !== '' && {
            modOutOfHoursEstimatedWeeklyHours: values.modOutOfHoursEstimatedWeeklyHours,
          }),

          ...(values.modAccommodationExpensesBudget !== '' && {
            modAccommodationExpensesBudget: values.modAccommodationExpensesBudget,
          }),
          ...(values.modOutOfHoursBudget !== '' && {
            modOutOfHoursBudget: values.modOutOfHoursBudget,
          }),
          ...(values.modSubsistenceExpensesBudget !== '' && {
            modSubsistenceExpensesBudget: values.modSubsistenceExpensesBudget,
          }),
          ...(values.modTravelExpensesBudget !== '' && {
            modTravelExpensesBudget: values.modTravelExpensesBudget,
          }),
        }
      }

      createShift({
        variables: { ...baseVariables, ...modVariables },
      })
        .then((response) => {
          const errors = response.data?.shiftBlockCreate?.errors

          if (errors.length > 0) {
            toast.update(2, { ...errorToast, render: `${errors[0].message}` })
            return
          }

          handleClose()
          resetForm()
          refetch(response?.data?.shiftBlockCreate?.shiftBlock)

          toast.update(2, { ...successToast, render: 'Block Created' })
        })
        .catch(() => {
          toast.update(2, { ...errorToast, render: 'Block could not be created' })
        })
    },
  })

  useQuery(GRADES, {
    skip: skipGradesQuery,
    variables: {
      staffGroupId: Number(formik?.values?.staffGroup[0]?.value),
      organisationId: organisation.id,
      orderBy: 'title',
    },
    onCompleted: (res) => {
      if (res) setGrades(res.grades)
      setSkipGrGradesQuery(true)
    },
    onError: () => {
      setGrades([])
    },
  })

  const calendarRange = modOrganisation ? {} : { before: new Date() }
  const orgStaffGroups = []

  if (details) {
    for (let x = 0; x < details.organisation.organisationStaffGroups.length; x += 1) {
      for (let y = 0; y < details.staffGroups.length; y += 1) {
        if (details.organisation.organisationStaffGroups[x].label === details.staffGroups[y].label) {
          orgStaffGroups.push({
            label: details.staffGroups[y].label,
            value: details.staffGroups[y].value,
          })
        }
      }
    }
  }

  return (
    <Modal backdrop="static" centered onHide={handleClose} show={showCreateModal} size="lg">
      <ModalHeader>
        <ModalTitle>Create New Block</ModalTitle>
        <CloseButton onClick={() => handleClose()} />
      </ModalHeader>

      <ModalBody style={{ minHeight: 500 }}>
        <FormGridContainer>
          <ShiftModalTitle>Block Info</ShiftModalTitle>

          <FormGrid columns={2}>
            <FormGroupMod>
              <FormLabel>
                Block Name <span>*</span>
              </FormLabel>
              <FormInput name="name" onChange={formik.handleChange} placeholder="Enter First Name..." type="text" />
              {formik.errors.name && <FormErrorLabel>{formik.errors.name}</FormErrorLabel>}
              {modOrganisation && modHelperTextFunction(0, false)}
            </FormGroupMod>

            <FormGroup>
              <FormLabel>
                Reason for Booking <span>*</span>
              </FormLabel>
              <FormSelect
                name="reason"
                onChange={(option) => {
                  formik.setFieldValue('reason', option !== null ? [option] : [])
                }}
                options={details && details.reasons.nodes.filter((r) => r.type === 'request')}
                placeholder="Reason for Booking"
              />
              {formik.errors.reason && <FormErrorLabel>{formik.errors.reason}</FormErrorLabel>}
            </FormGroup>
          </FormGrid>
        </FormGridContainer>

        <FormGridContainer>
          <FormGrid columns={2}>
            <FormGroup>
              <FormLabel>
                Start Date <span>*</span>
              </FormLabel>
              <DateInput
                name="startDate"
                onChange={(e) => formik.setFieldValue('startDate', e)}
                range={calendarRange}
                value={formik.values.startDate}
              />

              {formik.errors.startDate && <FormErrorLabel>{formik.errors.startDate}</FormErrorLabel>}
            </FormGroup>

            <FormGroup>
              <FormLabel>
                End Date <span>*</span>
              </FormLabel>
              <DateInput
                error={formik.errors.endDate}
                name="endDate"
                onChange={(e) => formik.setFieldValue('endDate', e)}
                range={calendarRange}
                value={formik.values.endDate}
              />

              {formik.errors.endDate && <FormErrorLabel>{formik.errors.endDate}</FormErrorLabel>}
            </FormGroup>
          </FormGrid>
        </FormGridContainer>

        <FormBreak />

        <FormGridContainer>
          <ShiftModalTitle>Clinical Info</ShiftModalTitle>

          <FormGrid columns={2} style={{ marginBottom: 16 }}>
            <FormGroupMod>
              <FormLabel>
                Staff Group <span>*</span>
              </FormLabel>
              <FormSelect
                name="reason"
                onChange={(option) => {
                  setSkipGrGradesQuery(false)
                  formik.setFieldValue('staffGroup', option !== null ? [option] : [])
                  formik.setFieldValue('grade', [])
                }}
                options={details && orgStaffGroups}
                placeholder="Select Staff Group"
              />

              {modOrganisation && modHelperTextFunction(1, false)}
              {formik.errors.reason && <FormErrorLabel>{formik.errors.reason}</FormErrorLabel>}
            </FormGroupMod>

            <FormGroupMod>
              <FormLabel>
                Grade <span>*</span>
              </FormLabel>
              <FormSelect
                name="grade"
                onChange={(option) => {
                  formik.setFieldValue('grade', option !== null ? [option] : [])
                }}
                options={sortedGrades}
                placeholder="Select Grade"
              />

              {modOrganisation && modHelperTextFunction(2, true)}
              {formik.errors.grade && <FormErrorLabel>{formik.errors.grade}</FormErrorLabel>}
            </FormGroupMod>
          </FormGrid>

          <FormGrid columns={2}>
            <FormGroupMod disabled={false}>
              <FormLabel>Booking Preference</FormLabel>
              <FormToggle
                action={() => {
                  formik.setFieldValue('bookingPreference', !formik.values.bookingPreference)
                }}
                label="Limit viewing to preferred workers"
                name="bookingPreference"
                value={formik.values.bookingPreference}
              />

              {modOrganisation && modHelperTextFunction(8, false)}
            </FormGroupMod>
          </FormGrid>

          {modOrganisation && (
            <>
              <FormBreak />
              <h2>MOD Custom Fields</h2>

              <FormGrid columns={2} style={{ marginBottom: 28 }}>
                <FormGroupMod>
                  <FormLabel>Out of Hours - Budget</FormLabel>
                  <FormInputWithSymbol
                    before
                    max="100"
                    min="0"
                    name="modOutOfHoursBudget"
                    onChange={formik.handleChange}
                    placeholder="0.00"
                    symbol=" £ "
                    type="number"
                  />

                  {modHelperTextFunction(3, false)}
                  {formik.errors?.modOutOfHoursBudget && (
                    <FormErrorLabel>{formik.errors.modOutOfHoursBudget}</FormErrorLabel>
                  )}
                </FormGroupMod>

                <FormGroupMod>
                  <FormLabel>Accommodation Expenses Budget</FormLabel>
                  <FormInputWithSymbol
                    before
                    max="100"
                    min="0"
                    name="modAccommodationExpensesBudget"
                    onChange={formik.handleChange}
                    placeholder="0.00"
                    symbol=" £ "
                    type="number"
                  />

                  {modHelperTextFunction(3, true)}
                  {formik.errors?.modOutOfHoursBudget && (
                    <FormErrorLabel>{formik.errors.modOutOfHoursBudget}</FormErrorLabel>
                  )}
                </FormGroupMod>
              </FormGrid>

              <FormGrid columns={2} style={{ marginBottom: 28 }}>
                <FormGroupMod>
                  <FormLabel>Subsistence Budget</FormLabel>
                  <FormInputWithSymbol
                    before
                    max="100"
                    min="0"
                    name="modSubsistenceExpensesBudget"
                    onChange={formik.handleChange}
                    placeholder="0.00"
                    symbol=" £ "
                    type="number"
                  />

                  {modHelperTextFunction(3, true)}
                  {formik.errors?.modSubsistenceExpensesBudget && (
                    <FormErrorLabel>{formik.errors.modSubsistenceExpensesBudget}</FormErrorLabel>
                  )}
                </FormGroupMod>

                <FormGroupMod>
                  <FormLabel>Travel Expenses Budget</FormLabel>
                  <FormInputWithSymbol
                    before
                    max="100"
                    min="0"
                    name="modTravelExpensesBudget"
                    onChange={formik.handleChange}
                    placeholder="0.00"
                    symbol=" £ "
                    type="number"
                  />

                  {modHelperTextFunction(3, true)}
                  {formik.errors?.modTravelExpensesBudget && (
                    <FormErrorLabel>{formik.errors.modTravelExpensesBudget}</FormErrorLabel>
                  )}
                </FormGroupMod>
              </FormGrid>

              <FormGrid columns={2} style={{ marginBottom: 28 }}>
                <FormGroupMod>
                  <FormLabel>Approved Weekly Hours</FormLabel>
                  <FormInput
                    name="modApprovalBudgetValue"
                    onChange={formik.handleChange}
                    placeholder="Approved Weekly hours"
                    type="number"
                  />

                  {formik.errors?.modApprovalBudgetValue && (
                    <FormErrorLabel>{formik.errors.modApprovalBudgetValue}</FormErrorLabel>
                  )}
                </FormGroupMod>

                <FormGroupMod>
                  <FormLabel>Out of Hours - Estimated Weekly Hours</FormLabel>
                  <FormInput
                    name="modOutOfHoursEstimatedWeeklyHours"
                    onChange={formik.handleChange}
                    placeholder="0.00"
                    type="number"
                  />

                  {modHelperTextFunction(3, true)}

                  {formik.errors?.modOutOfHoursEstimatedWeeklyHours && (
                    <FormErrorLabel>{formik.errors.modOutOfHoursEstimatedWeeklyHours}</FormErrorLabel>
                  )}
                </FormGroupMod>
              </FormGrid>

              <FormGrid columns={2} style={{ marginBottom: 28 }}>
                <FormGroupMod>
                  <FormLabel>Financial Approval Code</FormLabel>
                  <FormInput
                    name="modFinancialApprovalCode"
                    onChange={formik.handleChange}
                    placeholder="Approval Code"
                    type="text"
                  />

                  {modHelperTextFunction(4, true)}
                  {formik.errors?.modFinancialApprovalCode && (
                    <FormErrorLabel>{formik.errors.modFinancialApprovalCode}</FormErrorLabel>
                  )}
                </FormGroupMod>

                <FormGroupMod>
                  <FormLabel>Position Number</FormLabel>
                  <FormInput name="modPositionNumber" onChange={formik.handleChange} symbol="GBP" type="number" />

                  {modHelperTextFunction(5, true)}
                  {formik.errors?.modPositionNumber && (
                    <FormErrorLabel>{formik.errors.modPositionNumber}</FormErrorLabel>
                  )}
                </FormGroupMod>

                <FormGroupMod>
                  <FormLabel>Security Clearance</FormLabel>
                  <FormSelect
                    name="reason"
                    onChange={(option) => {
                      formik.setFieldValue('modSecurityClearance', option !== null ? [option] : [])
                    }}
                    options={securityClearance}
                    placeholder="Select Security Clearance"
                  />

                  {modHelperTextFunction(6, false)}
                  {formik.errors?.modSecurityClearance && (
                    <FormErrorLabel>{formik.errors.modSecurityClearance}</FormErrorLabel>
                  )}
                </FormGroupMod>
              </FormGrid>
            </>
          )}

          <FormBreak />
          <ShiftModalTitle>Notes</ShiftModalTitle>
          <FormGrid columns={2}>
            <FormGroupMod>
              <FormLabel>Worker Notes (Visible in Worker App)</FormLabel>
              <FormInput
                as="textarea"
                autoFocus
                name="workerNotes"
                onChange={formik.handleChange}
                placeholder="Enter notes here"
                rows={3}
              />

              <p style={{ fontSize: 12, marginTop: 6 }}>
                eg. Bleep numbers, who and where to report to, logins for computers and access codes for doors.
              </p>
              {modOrganisation && modHelperTextFunction(7, false)}
            </FormGroupMod>

            <FormGroup>
              <FormLabel>Admin notes (Visible only to Hub users)</FormLabel>
              <FormInput
                as="textarea"
                autoFocus
                name="adminNotes"
                onChange={formik.handleChange}
                placeholder="Enter notes here"
                rows={3}
              />
            </FormGroup>
          </FormGrid>
        </FormGridContainer>
      </ModalBody>

      <ModalFooter>
        <div />
        <div>
          <DefaultButton action={() => handleClose()} text="Cancel" type="white" />
          <DefaultButton
            action={() => {
              formik.handleSubmit()
              setValidate(true)
            }}
            color="blue"
            text="Create Block"
          />
        </div>
      </ModalFooter>
    </Modal>
  )
}

export default CreateBlockModal

export const ModInfoWrapper = styled.div`
  position: relative;
`

export const FormGroupMod = styled(FormGroup)`
  position: relative;

  opacity: ${({ disabled }) => (disabled ? `0.7 !important` : '1')};

  * {
    pointer-events: ${({ disabled }) => (disabled ? `none` : 'initial')};
  }

  svg:hover + div {
    opacity: 1 !important;
  }
`

export const ModWrapper = styled.span`
  cursor: pointer;
  height: 15px;
  position: absolute;
  right: 5px;
  top: -5px;
  width: 15px;
`

export const ToolTip = styled.div`
  background: #eaedf2;
  border: 1px solid #cbd2d9;
  border-radius: 8px;
  border-radius: 6px;
  left: ${(props) => (props.left ? '-360px' : '30px')};
  opacity: 0;
  padding: 12px;
  pointer-events: none;
  position: absolute;
  text-align: center;
  top: -30px;
  transition: all 0.7s ease-in-out;
  width: ${(props) => (props.short ? '150px' : '350px')};
  z-index: 100 !important;
`

export function HelpIcon() {
  return (
    <svg fill="none" height="24" viewBox="0 0 24 24" width="24" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M11.29 15.29C11.247 15.3375 11.2069 15.3876 11.17 15.44C11.1322 15.4957 11.1019 15.5563 11.08 15.62C11.0512 15.6767 11.031 15.7374 11.02 15.8C11.0151 15.8666 11.0151 15.9334 11.02 16C11.0166 16.1312 11.044 16.2613 11.1 16.38C11.1449 16.5041 11.2166 16.6168 11.3099 16.7101C11.4032 16.8034 11.5159 16.8751 11.64 16.92C11.7597 16.9729 11.8891 17.0002 12.02 17.0002C12.1509 17.0002 12.2803 16.9729 12.4 16.92C12.5241 16.8751 12.6368 16.8034 12.7301 16.7101C12.8234 16.6168 12.8951 16.5041 12.94 16.38C12.9844 16.2584 13.0048 16.1294 13 16C13.0008 15.8684 12.9755 15.7379 12.9258 15.6161C12.876 15.4943 12.8027 15.3834 12.71 15.29C12.617 15.1963 12.5064 15.1219 12.3846 15.0711C12.2627 15.0203 12.132 14.9942 12 14.9942C11.868 14.9942 11.7373 15.0203 11.6154 15.0711C11.4936 15.1219 11.383 15.1963 11.29 15.29ZM12 2C10.0222 2 8.08879 2.58649 6.4443 3.6853C4.79981 4.78412 3.51809 6.3459 2.76121 8.17317C2.00433 10.0004 1.8063 12.0111 2.19215 13.9509C2.578 15.8907 3.53041 17.6725 4.92894 19.0711C6.32746 20.4696 8.10929 21.422 10.0491 21.8079C11.9889 22.1937 13.9996 21.9957 15.8268 21.2388C17.6541 20.4819 19.2159 19.2002 20.3147 17.5557C21.4135 15.9112 22 13.9778 22 12C22 10.6868 21.7413 9.38642 21.2388 8.17317C20.7363 6.95991 19.9997 5.85752 19.0711 4.92893C18.1425 4.00035 17.0401 3.26375 15.8268 2.7612C14.6136 2.25866 13.3132 2 12 2ZM12 20C10.4178 20 8.87104 19.5308 7.55544 18.6518C6.23985 17.7727 5.21447 16.5233 4.60897 15.0615C4.00347 13.5997 3.84504 11.9911 4.15372 10.4393C4.4624 8.88743 5.22433 7.46197 6.34315 6.34315C7.46197 5.22433 8.88743 4.4624 10.4393 4.15372C11.9911 3.84504 13.5997 4.00346 15.0615 4.60896C16.5233 5.21447 17.7727 6.23984 18.6518 7.55544C19.5308 8.87103 20 10.4177 20 12C20 14.1217 19.1572 16.1566 17.6569 17.6569C16.1566 19.1571 14.1217 20 12 20ZM12 7C11.4731 6.99966 10.9553 7.13812 10.4989 7.40144C10.0425 7.66476 9.66347 8.04366 9.4 8.5C9.32765 8.61382 9.27907 8.7411 9.25718 8.87418C9.23529 9.00726 9.24055 9.14339 9.27263 9.27439C9.30472 9.40538 9.36297 9.52854 9.44389 9.63643C9.52481 9.74433 9.62671 9.83475 9.74348 9.90224C9.86024 9.96974 9.98945 10.0129 10.1233 10.0292C10.2572 10.0454 10.393 10.0345 10.5225 9.99688C10.6521 9.9593 10.7727 9.89591 10.8771 9.81052C10.9814 9.72513 11.0675 9.6195 11.13 9.5C11.2181 9.3474 11.345 9.22078 11.4978 9.13298C11.6505 9.04518 11.8238 8.9993 12 9C12.2652 9 12.5196 9.10536 12.7071 9.29289C12.8946 9.48043 13 9.73478 13 10C13 10.2652 12.8946 10.5196 12.7071 10.7071C12.5196 10.8946 12.2652 11 12 11C11.7348 11 11.4804 11.1054 11.2929 11.2929C11.1054 11.4804 11 11.7348 11 12V13C11 13.2652 11.1054 13.5196 11.2929 13.7071C11.4804 13.8946 11.7348 14 12 14C12.2652 14 12.5196 13.8946 12.7071 13.7071C12.8946 13.5196 13 13.2652 13 13V12.82C13.6614 12.58 14.2174 12.1152 14.5708 11.5069C14.9242 10.8985 15.0525 10.1853 14.9334 9.49189C14.8143 8.79849 14.4552 8.16902 13.919 7.71352C13.3828 7.25801 12.7035 7.00546 12 7Z"
        fill="#55606C"
      />
    </svg>
  )
}
